/**
 * The level's own nodes in the UI layer (M32, Kevin's screenshot of the Achievements sheet with the battle's thumb
 * cluster drawn over it): the HUD plate, the control row, the thumb cluster, the pills' flashes, the banners, the boss
 * bar, the card tray, the tower sheet, the edge markers, the pause layer, the end screen. Each part removes its own
 * nodes on dispose, but one throw part-way through the level's teardown (or a constructor that threw after the HUD
 * went up) left the rest on screen for the whole session, over the hub and its sheets.
 *
 * So the level also claims, as a set, every child it added to the layer (marked `data-level-ui`), and takes them all
 * away at the end whatever happened before; and the hub, as it is built, sweeps any marked node a level left behind
 * (`sweepLevelUi`). The shared overlays that outlive a level are never claimed: the guide's layer (`#guide`), toasts,
 * the cloud wipe, the level-load card, the fly layer and the unlock cards.
 */
export const LEVEL_UI_ATTR = "data-level-ui";

/** Children of the UI layer that are shared with the screens around a level, never the level's to take away. */
export function isSharedOverlay(e: Element): boolean {
  if (e.id === "guide") return true;
  return ["toast", "wipe", "lvload", "flylayer", "unlockcards"].some((c) => e.classList.contains(c));
}

/** Marks a node as the level's (the end screen, added after the level was built). */
export function markLevelUi(e: Element): void { e.setAttribute(LEVEL_UI_ATTR, ""); }

export class LevelUiScope {
  private readonly before: Set<Element>;
  private readonly claimed: Element[] = [];

  /** Notes what the layer holds before the level adds anything. */
  constructor(private readonly root: HTMLElement) {
    this.before = new Set(Array.from(root.children));
  }

  /** Marks every child added since the scope was made (shared overlays aside) as the level's. Repeatable. */
  claim(): void {
    for (const e of Array.from(this.root.children)) {
      if (this.before.has(e) || this.claimed.includes(e) || isSharedOverlay(e)) continue;
      markLevelUi(e);
      this.claimed.push(e);
    }
  }

  /** Removes every claimed node still on the page, and any other node marked as the level's. Returns how many. */
  release(): number {
    let n = 0;
    for (const e of this.claimed) if (e.parentNode) { e.remove(); n++; }
    this.claimed.length = 0;
    return n + sweepLevelUi(this.root);
  }
}

/** Removes the level nodes a level left in the UI layer (the hub calls it as it is built). Returns how many. */
export function sweepLevelUi(root: HTMLElement): number {
  const stale = Array.from(root.children).filter((e) => e.getAttribute(LEVEL_UI_ATTR) !== null);
  for (const e of stale) e.remove();
  return stale.length;
}

/**
 * What a hub left in the UI layer that a level must not start under: its sheets and their scrim, the achievement
 * banner (a tap on it would open the Achievements sheet over the battle). The hub's own teardown keeps an open sheet
 * on purpose (a hub rebuilt for a new fortress model keeps the sheet the player is on); a level start does not.
 */
export const HUB_LEFTOVERS = ["sheet", "sheet-scrim", "achbanner"];

/** Removes the hub's leftovers (HUB_LEFTOVERS) from the UI layer. Returns how many. */
export function clearHubLeftovers(root: HTMLElement): number {
  const stale = Array.from(root.children).filter((e) => HUB_LEFTOVERS.some((c) => e.classList.contains(c)) && e.getAttribute(LEVEL_UI_ATTR) === null);
  for (const e of stale) e.remove();
  return stale.length;
}
